import { lightTheme } from '@/customTheme'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import Icons from '../../../../Icons'
import ButtonCustom from '@/components/ButtonCustom'
import { SizeButton } from '@/constants/sizeButton'
import { KIND, SHAPE } from 'baseui/button'
import { selectOpenModalTryPremium, selectUser, selectUserIsPremium } from '@/store/slices/user/selectors'
import { useSelector } from 'react-redux'
import { PremiumOnPlatformType, SignInManager } from '../../SignInManager'
import { useAppDispatch } from '@/store/store'
import Loading from '@/components/Loading'
import { setOpenModalTryPremium } from '@/store/slices/user/actions'
import { useTranslation } from 'react-i18next'
import VideoIntroRemove from '@assets/video/intro-remove.mp4'
import { customAmplitude } from '@/utils/customAmplitude'
import { ShowModalType } from '@/scenes/engine/common/constants'
import useModal from '@/hooks/useModal'
import premiumPoster from "@assets/images/try_premium_frame_0.jpg"
import MetsiKonChoosePlan from './MetsiKonChoosePlan'
import ChoosePlanContent from './ChoosePlanContent'
import PaymentInfo from './PaymentInfo'
import TryPremiumSuccess from '../TryPremiumSuccess'
import { FALLBACK_PLAN_DETAIL, FALLBACK_SUPER_PLAN_DETAIL, getFallbackPlanDetail, getFallbackSuperPlanDetail } from './fallbackPlanDetail'
import { hasPhotoshootHandoffOrigin } from '@/utils/photoshootHandoffAttribution'
import { sanitizeCachedPlanDetailForTrialGating } from './planDetailTrialGating'
import {
  getIntroCtaKey,
  getIntroSubtitleKey,
  getIntroTitleKey,
  getPlanVariant,
  resolveInitialPlanType,
  LIMITED_TIME_OFFER_SOURCE,
  LIMITED_OFFER_PREMIUM_SOURCE,
  BATCH_LIMIT_SOURCE,
  AI_CREDITS_MENU_SUPER_SOURCE,
  type PlanType,
} from './metsikonPlanConfig'
import { completePostPurchaseLimitedTimeOffer } from '@/components/ShowModal/limitedTimeOffer'
import { maybeShowLimitedOfferAfterPaywallClose } from '@/components/ShowModal/LimitedOffer/limitedOfferTrigger'
import {
  AI_MODELS_HD_SOURCE,
} from '@/scenes/engine/utils/aiModelsAnalytics'
import { BATCH_UPLOAD_PREMIUM_SOURCE, WHATNOT_EXPORT_PREMIUM_SOURCE } from '@/scenes/BatchStudio/analytics/batchAnalytics'
import { BATCH_AI_TOOLS_SUPER_SOURCES } from '@/scenes/BatchStudio/analytics/batchAIToolsAnalytics'
import * as Sentry from '@sentry/react'

import { WrapAutoScroll } from '@/components/AutoScroll/AutoScroll'
import {
  getPostPurchaseMobileWarningPlatform,
  runUpgradeCallback,
  type MobilePremiumWarningPlatform,
} from './postPurchaseWarning'
import { buildPremiumCheckoutAnalyticsContext } from '@/utils/signupPremiumAnalytics'
import { EDITOR_DOWNLOAD_PREMIUM_SOURCE } from '@/services/download/editorDownloadPrompt'

const METSIKON_SOURCES = new Set([
  EDITOR_DOWNLOAD_PREMIUM_SOURCE,
  'BtSubscriptionFonts',
  'BtSubscriptionBackgrounds',
  'BtSubscriptionTemplates',
  'Header_CTA',
  'Header_CTA_Super',
  AI_CREDITS_MENU_SUPER_SOURCE,
  'Create Header_CTA',
  'Editor Open',
  'Create',
  'BtSubscriptionWatermark',
  'BtSubscriptionCanvasSize',
  'BtSubscriptionStickers',
  'Scratch',
  'BtSubscriptionPhotoshoot',
  'BtSubscriptionOutOfCredits',
  'BtSubscriptionAIHD',
  'BtSubscriptionAIVideo',
  AI_MODELS_HD_SOURCE,
  'BtSubscriptionBatchMagic',
  WHATNOT_EXPORT_PREMIUM_SOURCE,
  BATCH_UPLOAD_PREMIUM_SOURCE,
  ...BATCH_AI_TOOLS_SUPER_SOURCES,
  LIMITED_TIME_OFFER_SOURCE,
  LIMITED_OFFER_PREMIUM_SOURCE,
  BATCH_LIMIT_SOURCE,
])

function Metsikon({handleOpenTutorial, pendingPlanRef}: {handleOpenTutorial: () => void, pendingPlanRef: React.MutableRefObject<{ plan: number; planDetail: any; planType?: PlanType } | null>}) {
  const user = useSelector(selectUser)
  const openModalTryPremium = useSelector(selectOpenModalTryPremium)
  const [isOpenPayment, setIsOpenPayment] = useState(false)
  const [isOpenChooseAPlan, setIsOpenChooseAPlan] = useState(() => {
    const isAutoOpenSource = METSIKON_SOURCES.has(openModalTryPremium.source) || openModalTryPremium.source === 'Comeback'
    if (!isAutoOpenSource) {
      return false
    }
    const hasPendingPlan = pendingPlanRef.current != null && user != null
    const isUpgradeFromPremium = Boolean(user?.userIsPremium) && !Boolean(user?.userIsSuper)
    // Premium -> Super should always require explicit plans confirmation.
    // Only free-user resume-after-sign-in should skip directly to payment.
    return !hasPendingPlan || isUpgradeFromPremium
  })
  // Optimistic seed: show env-driven prices/IDs immediately so the modal never
  // paints with a blank price while Paddle.PricePreview resolves. The async
  // effect below replaces this with Paddle-localized prices when ready.
  // Withhold the yearly trial seed for photoshoot-handoff sessions too, so the
  // fallback matches what SignInManager.getYearlyPlan() will resolve to (see
  // SignInManager.shouldWithholdYearlyTrial) and avoids a trial-then-no-trial flicker.
  const shouldWithholdYearlyTrialSeed = !!user?.userHadAFreeTrial || hasPhotoshootHandoffOrigin()
  const [planDetail, setPlanDetail] = useState(() => {
    if (pendingPlanRef.current?.planType === 'super') {
      return getFallbackPlanDetail(shouldWithholdYearlyTrialSeed)
    }
    const cachedPlanDetail = sanitizeCachedPlanDetailForTrialGating(
      pendingPlanRef.current?.planDetail,
      shouldWithholdYearlyTrialSeed,
    )
    return cachedPlanDetail ?? getFallbackPlanDetail(shouldWithholdYearlyTrialSeed)
  })
  const [superPlanDetail, setSuperPlanDetail] = useState(
    () => pendingPlanRef.current?.planType === 'super'
      ? (pendingPlanRef.current?.planDetail ?? getFallbackSuperPlanDetail())
      : getFallbackSuperPlanDetail(),
  )
  const [planActive, setPlanActive] = useState(1)
  const [planType, setPlanType] = useState<PlanType>(
    () => pendingPlanRef.current?.planType ?? resolveInitialPlanType(openModalTryPremium.source),
  )
  
  // The limited offer checks out one plan (yearly Premium without trial, with its
  // discount) straight from the payment form, so it brings its own plan detail.
  const isLimitedOffer = openModalTryPremium.source === LIMITED_OFFER_PREMIUM_SOURCE && Boolean(user)
  const [offerPlanDetail, setOfferPlanDetail] = useState<any>(null)
  const activePlanDetail = isLimitedOffer ? offerPlanDetail : planType === 'super' ? superPlanDetail : planDetail
  const pricingPlanDetail = activePlanDetail ?? (planType === 'super' ? FALLBACK_SUPER_PLAN_DETAIL : FALLBACK_PLAN_DETAIL)
  const yearlyFromMonthlyAmount = pricingPlanDetail.month?.priceAmount * 12

  const hasPremium = useSelector(selectUserIsPremium)
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { setShowModalType, setShowModalCustomText } = useModal()

  const [justPurchased, setJustPurchased] = useState(false)
  const prevHasPremiumRef = useRef(hasPremium)
  const pendingPostPurchaseWarningPlatformRef = useRef<MobilePremiumWarningPlatform | null>(null)
  // Guards "Back from payment" so checkout.closed doesn't kick user to Intro.
  const isBackNavigationCloseRef = useRef(false)
  // Keeps a short-lived marker so delayed checkout.closed events from Back are
  // still treated as "return to plans", even after a fast Back -> Continue loop.
  const backNavigationCloseRequestedAtRef = useRef<number | null>(null)

  const closeTryPremiumModal = useCallback(() => {
    dispatch(
      setOpenModalTryPremium({
        ...openModalTryPremium,
        source: null,
        callback: null,
        continuation: undefined,
        isOpen: false,
      })
    )
    setIsOpenPayment(false)
    isBackNavigationCloseRef.current = false
    backNavigationCloseRequestedAtRef.current = null
  }, [dispatch, openModalTryPremium])

  const runPendingUpgradeCallback = useCallback(() => {
    if (!openModalTryPremium.source || !(openModalTryPremium.continuation ? hasPremium : user?.userIsPremium)) return
    runUpgradeCallback(
      openModalTryPremium.callback as (() => unknown | Promise<unknown>) | null | undefined
    )
  }, [openModalTryPremium.source, openModalTryPremium.callback, openModalTryPremium.continuation, hasPremium, user?.userIsPremium])

  const finalizePostPurchaseFlow = useCallback(
    (
      warningPlatform: MobilePremiumWarningPlatform | null,
      runTutorialAfterDismiss: boolean,
    ) => {
      const continueFlow = runTutorialAfterDismiss ? () => handleOpenTutorial() : undefined
      if (warningPlatform == null) {
        continueFlow?.()
        return
      }
      const description =
        warningPlatform === PremiumOnPlatformType.android
          ? t('To avoid being charged twice, cancel your Premium subscription in Google Play.')
          : t('To avoid being charged twice, cancel your Premium subscription in the App Store.')

      setShowModalCustomText(
        t('Cancel your old Premium plan'),
        description,
        t('Ok'),
        undefined,
        false,
        continueFlow,
        continueFlow,
      )
    },
    [handleOpenTutorial, setShowModalCustomText, t],
  )

  useEffect(() => {
    const wasPremium = prevHasPremiumRef.current
    prevHasPremiumRef.current = hasPremium
    if (hasPremium && !wasPremium) {
      setJustPurchased(true)
    }
    return () => {
      setIsOpenPayment(false)
    }
  }, [hasPremium])

  useEffect(() => {
    if (!openModalTryPremium.isOpen) {
      setJustPurchased(false)
      isBackNavigationCloseRef.current = false
      backNavigationCloseRequestedAtRef.current = null
      pendingPostPurchaseWarningPlatformRef.current = null
    }
  }, [openModalTryPremium.isOpen])

  useEffect(() => {
    // Only (re)fetch while the paywall is actually open. Metsikon can live in
    // a persistent modal shell mounted well before route-level entry points
    // (Sso, Photoshoots) capture the photoshoot-handoff origin marker, so a
    // fetch made only on mount/`userHadAFreeTrial` change can be stale by the
    // time the user actually reaches the paywall. Re-running on `isOpen`
    // guarantees a fresh check exactly when pricing/trial correctness
    // matters -- SignInManager.getPlanPrices() itself is cheap to call again
    // since it caches by shouldWithholdYearlyTrial() and only re-fetches when
    // that value actually changed.
    if (!openModalTryPremium.isOpen) return
    let cancelled = false
    SignInManager.getInstance().getPlanPrices()
      .then(({ monthly: monthlyPlan, yearly: yearlyPlan, monthlySuper: monthlySuperPlan, yearlySuper: yearlySuperPlan }) => {
        if (cancelled) return
        setPlanDetail({
          // Currency / country are the same for both plans (driven by the
          // customer's location). Prefer yearly, then monthly, so derived
          // prices in MetsiKonChoosePlan format like Paddle's headline.
          currencyCode: yearlyPlan?.recurring?.currencyCode ?? monthlyPlan?.recurring?.currencyCode,
          countryCode: yearlyPlan?.recurring?.countryCode ?? monthlyPlan?.recurring?.countryCode,
          month: {
            price: monthlyPlan?.recurring?.price?.gross,
            priceAmount: monthlyPlan?.recurring?.price?.amount,
            id: monthlyPlan.plan_id,
            canFreeTrial: monthlyPlan.recurring.subscription.trial_days > 0,
            trialDays: monthlyPlan?.recurring?.subscription?.trial_days ?? 0,
          },
          year: {
            price: yearlyPlan?.recurring?.price?.gross,
            priceAmount: yearlyPlan?.recurring?.price?.amount,
            id: yearlyPlan.plan_id,
            canFreeTrial: yearlyPlan.recurring.subscription.trial_days > 0,
            trialDays: yearlyPlan?.recurring?.subscription?.trial_days ?? 0,
          },
        })
        setSuperPlanDetail({
          currencyCode: yearlySuperPlan?.recurring?.currencyCode ?? monthlySuperPlan?.recurring?.currencyCode,
          countryCode: yearlySuperPlan?.recurring?.countryCode ?? monthlySuperPlan?.recurring?.countryCode,
          month: {
            price: monthlySuperPlan?.recurring?.price?.gross,
            priceAmount: monthlySuperPlan?.recurring?.price?.amount,
            id: monthlySuperPlan.plan_id,
            canFreeTrial: monthlySuperPlan.recurring.subscription.trial_days > 0,
            trialDays: monthlySuperPlan?.recurring?.subscription?.trial_days ?? 0,
          },
          year: {
            price: yearlySuperPlan?.recurring?.price?.gross,
            priceAmount: yearlySuperPlan?.recurring?.price?.amount,
            id: yearlySuperPlan.plan_id,
            canFreeTrial: yearlySuperPlan.recurring.subscription.trial_days > 0,
            trialDays: yearlySuperPlan?.recurring?.subscription?.trial_days ?? 0,
          },
        })
      })
      .catch(() => { /* keep optimistic env-seeded planDetail on failure */ })
    return () => { cancelled = true }
  }, [user?.userHadAFreeTrial, openModalTryPremium.isOpen])

  useEffect(() => {
    if (!openModalTryPremium.isOpen || !isLimitedOffer) {
      setOfferPlanDetail(null)
      return
    }
    let cancelled = false
    SignInManager.getInstance().getLimitedOfferPrices()
      .then(({ priceId, regular, discounted, discountId }) => {
        if (cancelled) return
        const price = discounted ?? regular
        setOfferPlanDetail({
          currencyCode: price.currencyCode,
          countryCode: price.countryCode,
          year: {
            price: price.display,
            priceAmount: price.amount,
            id: priceId,
            canFreeTrial: false,
            trialDays: 0,
            discountId: discountId ?? undefined,
          },
        })
      })
      .catch(() => { /* the paywall stays on its loading placeholder */ })
    return () => { cancelled = true }
  }, [openModalTryPremium.isOpen, isLimitedOffer])

  const setupPreOpenPaddle = useCallback(
    (overridePlan?: number) => {
      const currentPlanDetail = isLimitedOffer ? offerPlanDetail : planType === 'super' ? superPlanDetail : planDetail
      if (!user?.userId || !currentPlanDetail) {
        return
      }
      if (!document.querySelector('.paddle-checkout')) {
        Sentry.captureException(
          new Error('setupPreOpenPaddle: .paddle-checkout container not found; aborting checkout open'),
          {
            level: 'error',
            extra: {
              source: openModalTryPremium.source,
              planType,
              hasUserId: Boolean(user?.userId),
            },
          },
        )
        return
      }
      const plan = overridePlan ?? planActive
      const isUpgradeToSuper = planType === 'super' && Boolean(user.userIsPremium) && !Boolean(user.userIsSuper)
      const checkoutType = isUpgradeToSuper ? 'Upgrade to Super' : 'Standard'
      const analytics = buildPremiumCheckoutAnalyticsContext({
        source: openModalTryPremium.source,
        type: checkoutType,
        duration: plan === 1 ? 'Monthly' : 'Yearly',
        email: user.email,
      })
      pendingPostPurchaseWarningPlatformRef.current = getPostPurchaseMobileWarningPlatform(
        planType,
        user.userIsPremium,
        user.userIsSuper,
        user.userPremiumOnPlatform,
      )
      SignInManager.getInstance().pay(
        user.platformType,
        user.email,
        user.userId,
        (completed?: boolean) => {
          const backCloseRequestedAt = backNavigationCloseRequestedAtRef.current
          const isRecentBackCloseRequest =
            backCloseRequestedAt != null && Date.now() - backCloseRequestedAt <= 5000
          const isBackNavigationClose = isBackNavigationCloseRef.current || isRecentBackCloseRequest
          // Consume guard + token immediately so they cannot leak into later close events.
          isBackNavigationCloseRef.current = false
          backNavigationCloseRequestedAtRef.current = null
          if (!completed) {
            pendingPostPurchaseWarningPlatformRef.current = null
          }
          if (isBackNavigationClose) {
            setIsOpenPayment(true)
            setIsOpenChooseAPlan(true)
            return
          }
          setIsOpenPayment(false)
        },
        plan === 1 ? currentPlanDetail?.month?.id : currentPlanDetail?.year?.id,
        analytics,
        plan === 1 ? currentPlanDetail?.month?.discountId : currentPlanDetail?.year?.discountId,
      )
      const selectedPlanPrice = plan === 1 ? currentPlanDetail?.month?.price : currentPlanDetail?.year?.price
      const selectedPlanPriceAmount =
        plan === 1 ? currentPlanDetail?.month?.priceAmount : currentPlanDetail?.year?.priceAmount
      const selectedPlan = plan === 1 ? 'Monthly' : 'Yearly'
      const selectedPlanPayload = {
        Source: openModalTryPremium.source ?? 'Unknown',
        Plan: selectedPlan,
        Price: selectedPlanPrice,
        PriceAmount: selectedPlanPriceAmount,
        Currency: currentPlanDetail?.currencyCode,
        Type: checkoutType,
      }
      customAmplitude('Premium Selected Plan', selectedPlanPayload)
    },
    [planActive, planDetail, superPlanDetail, planType, user, openModalTryPremium.source, isLimitedOffer, offerPlanDetail]
  )

  useEffect(() => {
    customAmplitude('Webapp_split_test_Onboarding_metsikon', {
      user: 'Metsikon_Plans'
    })
  }, [])

  useEffect(() => {
    if (isLimitedOffer) {
      setPlanType('premium')
      setPlanActive(2)
      setIsOpenPayment(true)
      setIsOpenChooseAPlan(false)
      return
    }
    const isMetsikon = METSIKON_SOURCES.has(openModalTryPremium.source) || openModalTryPremium.source === 'Comeback'
    if (isMetsikon && planDetail) {
      setIsOpenPayment(true)
      if (pendingPlanRef.current != null && user) {
        const { plan: planToUse, planDetail: cachedPlanDetail, planType: cachedPlanType } = pendingPlanRef.current
        const resolvedPlanType = cachedPlanType ?? resolveInitialPlanType(openModalTryPremium.source)
        if (cachedPlanDetail) {
          if (resolvedPlanType === 'super') {
            setSuperPlanDetail(cachedPlanDetail)
          } else {
            const sanitizedPlanDetail = sanitizeCachedPlanDetailForTrialGating(
              cachedPlanDetail,
              shouldWithholdYearlyTrialSeed,
            )
            // If sanitization rejects the cache (stale trial-enabled yearly
            // price under withholding), keep whatever planDetail is already
            // in state -- it was seeded/fetched with gating already applied.
            if (sanitizedPlanDetail) {
              setPlanDetail(sanitizedPlanDetail)
            }
          }
        }
        setPlanType(resolvedPlanType)
        setPlanActive(planToUse)
        const isUpgradeFromPremium = Boolean(user.userIsPremium) && !Boolean(user.userIsSuper)
        setIsOpenChooseAPlan(isUpgradeFromPremium)
        pendingPlanRef.current = null
      } else {
        setPlanType(resolveInitialPlanType(openModalTryPremium.source))
        if (!isOpenPayment) {
          setIsOpenChooseAPlan(true)
        }
      }
    } else if (!isMetsikon) {
      isBackNavigationCloseRef.current = false
      backNavigationCloseRequestedAtRef.current = null
      setIsOpenPayment(false)
      setIsOpenChooseAPlan(false)
      setPlanType(resolveInitialPlanType(openModalTryPremium.source))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModalTryPremium.source, planDetail, user, pendingPlanRef, isLimitedOffer])

  const isAutoOpen = METSIKON_SOURCES.has(openModalTryPremium.source) || openModalTryPremium.source === 'Comeback'
  const contentReady = !((isAutoOpen && !planDetail) || (isLimitedOffer && !offerPlanDetail))
  const continuation = openModalTryPremium.continuation
  useEffect(() => {
    if (!openModalTryPremium.isOpen || !contentReady || !continuation) return
    let frame = 0
    const checkVisible = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        if (document.visibilityState !== 'visible') return
        const modal = document.getElementById('modal-try-premium')
        if (!modal) return
        const rect = modal.getBoundingClientRect()
        const hit = document.elementFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2)
        if (rect.width > 0 && rect.height > 0 && hit && modal.contains(hit)) continuation.onVisible()
      })
    }
    checkVisible()
    document.addEventListener('visibilitychange', checkVisible)
    return () => {
      cancelAnimationFrame(frame)
      document.removeEventListener('visibilitychange', checkVisible)
    }
  }, [openModalTryPremium.isOpen, contentReady, continuation])
  if (!contentReady) {
    return <><div id="modal-try-premium" style={{ visibility: 'hidden' }} /></>
  }

  const effectiveIsOpenPayment = isAutoOpen ? true : isOpenPayment
  const effectiveIsOpenChooseAPlan = isOpenChooseAPlan

  const planVariant = getPlanVariant(planType)
  const choosePlanGradient = planVariant.choosePlanGradient

  return (
    <>
      <div
        style={{
          width: '100vw',
          height: '100vh',
          maxWidth: '920px ',
          maxHeight: '652px',
          minWidth: '920px ',
          minHeight: '652px',
          flexDirection: 'row',
          background: 'rgba(255, 255, 255, 1)',
          borderRadius: '16px',
          overflow: 'hidden',
          position: 'absolute',
          display: 'flex'
        }}
        id="modal-try-premium"
      >
        {(!user?.userIsPremium || !user?.userIsSuper) && !justPurchased ? (
          <>
            {/* left */}
            {!effectiveIsOpenPayment ? (
              <IntroPanel
                planType={planType}
                user={user}
                withholdYearlyTrial={shouldWithholdYearlyTrialSeed}
                onCtaClick={() => {
                  isBackNavigationCloseRef.current = false
                  setIsOpenChooseAPlan(true)
                  setIsOpenPayment(true)
                }}
                onSignInClick={() => {
                  isBackNavigationCloseRef.current = false
                  setShowModalType(ShowModalType.SIGN_IN)
                  dispatch(setOpenModalTryPremium({ ...openModalTryPremium, isOpen: false }))
                }}
              />
            ) : !effectiveIsOpenChooseAPlan ? (
              <PaymentForm
                onBackToPlans={() => {
                  // The offer has no plan chooser to go back to.
                  if (isLimitedOffer) {
                    closeTryPremiumModal()
                    return
                  }
                  isBackNavigationCloseRef.current = true
                  backNavigationCloseRequestedAtRef.current = Date.now()
                  setIsOpenPayment(true)
                  setIsOpenChooseAPlan(true)
                }}
                setupPreOpenPaddle={setupPreOpenPaddle}
              />
            ) : (
              <MetsiKonChoosePlan
                onConfirmPlan={() => {
                  if (!user) {
                    isBackNavigationCloseRef.current = false
                    pendingPlanRef.current = { plan: planActive, planDetail: activePlanDetail, planType }
                    setIsOpenChooseAPlan(false)
                    setShowModalType(ShowModalType.SIGN_IN)
                    dispatch(
                      setOpenModalTryPremium({
                        ...openModalTryPremium,
                        isOpen: false,
                        callback: () => { handleOpenTutorial() },
                      })
                    )
                  } else {
                    isBackNavigationCloseRef.current = false
                    setIsOpenChooseAPlan(false)
                    setIsOpenPayment(true)
                  }
                }}
                onNavigate={() => { setIsOpenPayment(false) }}
                planDetail={activePlanDetail}
                yearlyFromMonthlyAmount={yearlyFromMonthlyAmount}
                planActive={planActive}
                setPlanActive={setPlanActive}
                planType={planType}
                setPlanType={setPlanType}
              />
            )}
            {/* right */}
            <div
              style={{
                width: '50%',
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                background: (effectiveIsOpenPayment && effectiveIsOpenChooseAPlan) ? choosePlanGradient : 'rgba(249, 249, 249, 1)',
              }}
            >
              {!effectiveIsOpenPayment ? (
                planVariant.introVideo ? (
                  <video
                    width="100%"
                    height="100%"
                    style={{ objectFit: 'cover' }}
                    loop
                    autoPlay
                    muted
                    playsInline
                    src={planVariant.introVideo}
                  />
                ) : openModalTryPremium.placeholderImage && !openModalTryPremium.video ? (
                  <img
                    width="100%"
                    height="100%"
                    style={{
                      objectFit: 'cover',
                    }}
                    src={openModalTryPremium.placeholderImage}
                    alt=""
                  />
                ) : (
                  <video
                    width="100%"
                    height="100%"
                    style={{
                      objectFit: 'cover',
                    }}
                    loop
                    autoPlay
                    muted
                    playsInline
                    src={openModalTryPremium.video ? openModalTryPremium.video : VideoIntroRemove}
                    poster={openModalTryPremium.placeholderImage ? openModalTryPremium.placeholderImage : premiumPoster}
                  />
                )
              ) : effectiveIsOpenChooseAPlan ? (
                <ChoosePlanContent planActive={planActive} planDetail={activePlanDetail} planType={planType} yearlyFromMonthlyAmount={yearlyFromMonthlyAmount} />
              ) : (
                <PaymentInfo planActive={planActive} planDetail={activePlanDetail} planType={planType} />
              )}
            </div>
            {/* close icon */}
            <div
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
              }}
              onClick={e => {
                e.stopPropagation()
                pendingPlanRef.current = null
                isBackNavigationCloseRef.current = false
                backNavigationCloseRequestedAtRef.current = null
                pendingPostPurchaseWarningPlatformRef.current = null
                setPlanActive(1)
                setPlanType('premium')
                continuation?.onVisible()
                customAmplitude('Premium Prompt Dismissed', { Source: openModalTryPremium.source ?? 'Unknown' })
                continuation?.onDismiss()
                dispatch(
                  setOpenModalTryPremium({
                    isOpen: false,
                    source: null,
                    opened: openModalTryPremium.opened
                  })
                )
                setIsOpenPayment(false)
                if (continuation) return
                handleOpenTutorial()
                // Runs after the tutorial hand-off, which keeps the screen if it opens a step.
                // Closing the offer's own checkout must not bring the offer straight back.
                if (openModalTryPremium.source !== LIMITED_OFFER_PREMIUM_SOURCE) maybeShowLimitedOfferAfterPaywallClose()
              }}
            >
              <Icons.WhiteCloseIcon size={24} />
            </div>
          </>
        ) : (
          <TryPremiumSuccess
            planType={user?.userIsSuper ? 'super' : 'premium'}
            close={() => {
              const warningPlatform = pendingPostPurchaseWarningPlatformRef.current
              pendingPostPurchaseWarningPlatformRef.current = null
              continuation?.onVisible()
              runPendingUpgradeCallback()
              closeTryPremiumModal()
              if (continuation) {
                finalizePostPurchaseFlow(warningPlatform, false)
                return
              }
              // A fresh monthly Premium subscriber gets the one-time
              // limited-time Super offer first; it takes over the tutorial
              // hand-off when it opens. `planActive` is 1 = monthly, 2 = yearly.
              void completePostPurchaseLimitedTimeOffer({
                purchasedPlanType: planType,
                purchasedPeriod: planActive === 1 ? 'month' : 'year',
                userIsPremium: user?.userIsPremium,
                userIsSuper: user?.userIsSuper,
                onDismiss: () => finalizePostPurchaseFlow(warningPlatform, true),
              })
            }}
          />
        )}
      </div>
    </>
  )
}

export default Metsikon

function PaymentForm({
  onBackToPlans,
  setupPreOpenPaddle,
}: {
  onBackToPlans: () => void
  setupPreOpenPaddle: () => void
}) {
  const { t } = useTranslation()
  // Guard against re-opening the Paddle iframe when setupPreOpenPaddle's identity
  // changes due to background Redux updates (user profile refresh, post-checkout
  // setUser, etc.). The checkout must be opened exactly once per PaymentForm mount.
  const openedRef = useRef(false)

  useEffect(() => {
    customAmplitude('Premium Payment info', {
      type: 'Standard',
    })
  }, [])

  // Open Paddle after this panel mounts so Billing inline checkout finds `.paddle-checkout`.
  useEffect(() => {
    if (openedRef.current) return
    openedRef.current = true
    const timeoutId = window.setTimeout(() => {
      setupPreOpenPaddle()
    }, 0)
    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [setupPreOpenPaddle])

  // Ensure the Billing iframe is torn down if React unmounts the panel
  // mid-checkout (e.g., parent state churn). Prevents a ghost iframe surviving
  // outside the React tree.
  useEffect(() => {
    return () => {
      try {
        // @ts-ignore Paddle Billing exposes Checkout.close at runtime
        window.Paddle?.Checkout?.close?.()
      } catch {
        /* ignore */
      }
    }
  }, [])
  
  return (
    <WrapAutoScroll
      style={{
        position: 'relative',
        padding: '48px',
        paddingRight: '32px',
        display: 'flex',
        flexDirection: 'column',
        width: '50%',
        textAlign: 'left',
        overflowY: 'scroll',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'start', height: '32px', gap: '8px' }}>
        <ButtonCustom
          shape={SHAPE.square}
          kind={KIND.minimal}
          type={SizeButton.SMALL}
          onClick={() => {
            onBackToPlans()
          }}
        >
          <Icons.Back size={24} fill={lightTheme.colors.blackGray} />
        </ButtonCustom>
        <span style={{ ...lightTheme.typography.Header26bold }}>{t('Payment info')}</span>
      </div>
      <div
        id="paddle-checkout-loader"
        style={{
          display: 'none',
          position: 'absolute',
          bottom: '50%',
          left: '50%',
          transform: 'translate(-50%, 0%)',
          paddingLeft: '12px',
        }}
      >
        <Loading fill="#BCBCBC" />
      </div>
      <div id="paddle-checkout" className="paddle-checkout" />
    </WrapAutoScroll>
  )
}

function IntroPanel({
  planType,
  user,
  withholdYearlyTrial,
  onCtaClick,
  onSignInClick,
}: {
  planType: PlanType
  user: ReturnType<typeof selectUser>
  withholdYearlyTrial: boolean
  onCtaClick: () => void
  onSignInClick: () => void
}) {
  const { t } = useTranslation()

  const variant = getPlanVariant(planType)
  const isSuper = planType === 'super'
  const buttonStyle = variant.introButtonGradient
    ? { background: variant.introButtonGradient }
    : {}

  return (
    <WrapAutoScroll
      style={{
        padding: '48px 40px 16px 48px',
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'left',
        width: '50%',
      }}
    >
      <h2 style={{ margin: 0, ...lightTheme.typography.Header26bold }}>
        {t(getIntroTitleKey(planType))}
      </h2>
      <h4
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '8px',
          margin: '4px 0 4px 0',
          ...lightTheme.typography.Small14regular,
          fontFamily: 'ES-Rebond-Grotesque-Classic, Rubik, Roboto',
        }}
      >
        {t(getIntroSubtitleKey(planType))}
      </h4>
      {variant.features.map((item, index) => (
        <h4
          key={index}
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '8px',
            margin: '20px 0 0 0',
          }}
        >
          <Icons.CheckIcon fill={variant.checkColor} />
          <span style={{ ...lightTheme.typography.Regular16 }}>{t(item.key, item.options)}</span>
        </h4>
      ))}
      <div style={{ marginTop: 'auto', textAlign: 'center', marginBottom: user || isSuper ? '32px' : '0' }}>
        <ButtonCustom
          type={SizeButton.LARGE}
          kind={KIND.primary}
          style={{
            position: 'relative',
            ...lightTheme.typography.Small14Semibold,
            color: '#fff',
            marginBottom: 0,
            marginTop: '8px',
            ...buttonStyle,
          }}
          onClick={onCtaClick}
        >
          {t(getIntroCtaKey(planType, Boolean(user?.userHadAFreeTrial) || withholdYearlyTrial))}
        </ButtonCustom>
        {!user && !isSuper ? (
          <h4
            style={{
              marginBottom: 0,
              marginTop: '8px',
              ...lightTheme.typography.Small12medium,
              padding: '0 16px',
              color: 'rgba(153, 153, 153, 1)',
              cursor: 'pointer',
            }}
            onClick={onSignInClick}
          >
            <span style={{ fontWeight: '400' }}>{t('Already Premium')}?</span> {t('Sign in')}
          </h4>
        ) : null}
      </div>
    </WrapAutoScroll>
  )
}
